import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { PaymentCreate } from "@steelbill/types";
import { api } from "@/lib/api";
import { ApiError } from "@/lib/api-error";
import {
  endpoints,
  type CustomerStatementResponse,
  type InvoicePaymentSummary,
  type InvoicePaymentsResponse,
  type OutstandingResponse,
  type PaymentResponse,
} from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * Payment tracking, from the client's side (§17.5).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A PAYMENT IS A LEDGER ROW, NOT A STATUS, AND THIS FILE NEVER FORGETS IT.
 *
 * `InvoiceStatus` is DRAFT / PENDING_IRN / ISSUED / CANCELLED and stays that
 * way: those are facts about the tax document. Being paid is a fact about the
 * BANK. Nothing here writes, patches or derives an invoice status, and nothing
 * here stores a balance — "paid", "partly paid" and "outstanding" arrive
 * DERIVED from the server on every read, as `grand_total_paise` minus the sum
 * of the ledger. A cached copy and a ledger will disagree eventually and the
 * ledger is the one that is right.
 *
 * NO ARITHMETIC LIVES IN THIS FILE. Not a subtraction, not a `reduce`, not a
 * "quick" outstanding for a button's disabled state. §6 makes the server the
 * only authority on money, and the one number the owner would check against his
 * bank is the last one a screen should be inventing. Every figure the payment
 * screens render comes off the wire already computed — `summary`, `byCustomer`
 * and `totals` included.
 *
 * A 404 FROM ANY OF THESE MEANS THE MODULE IS SWITCHED OFF, NOT "no such bill".
 * Every payment route sits behind `requireFlag("payments")`, which runs BEFORE
 * `requireCap` and answers 404 rather than 403 so that "forbidden" cannot leak
 * the existence of unreleased work (§18.3). There is deliberately no `useFlag`
 * hook and no `GET /flags` query behind these — that endpoint needs
 * `settings.write`, which the OWNER does not hold, so asking it would 403 for
 * the one person using the app. {@link isFeatureOff} reads the 404 instead, and
 * every surface renders NOTHING rather than "you do not have permission", which
 * is exactly what a leaked 403 would look like.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/**
 * Cache keys for the payment screens.
 *
 * FOLDED INTO `lib/query-keys.ts` ON 2026-08-16 and re-exported here so no call
 * site changed. It lived locally "with the feature", following `challanKeys`,
 * which had the same note — and two features opting out of a file whose stated
 * purpose is "spelled once" is how the file stops being the answer to "what do I
 * invalidate after this write". Key values are unchanged, so nothing cached was
 * orphaned.
 *
 * Note what is still ABSENT there and correctly so: no `list(filters)`, because
 * `GET /payments/outstanding` takes no parameters; and no key parameterised by
 * document kind, because payments attach to INVOICES only (§17.5).
 */
export const paymentKeys = queryKeys.payments;

/**
 * Is this failure "the `payments` module is off" rather than a real error?
 *
 * §18.3 — the flag answers 404 so that a disabled feature is byte-identical to a
 * route that was never mounted. The client half of that contract is that a 404
 * from a payment endpoint renders nothing at all: no alert, no retry button
 * (trying again cannot switch a flag on), and above all no "you do not have
 * permission", which would leak precisely what the 404 exists to hide.
 */
export function isFeatureOff(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * `GET /invoices/:id/payments` — the receipts against one bill, oldest first,
 * and where the bill stands.
 *
 * ONE REQUEST SERVES THE WHOLE INVOICE SCREEN. The detail page needs the
 * position (for the badge beside the status and the figures under the totals)
 * AND the history (for the card), and this answers both. `GET /invoices/:id`
 * also carries a `payment` summary as a SIBLING of `data`, but the screen does
 * not read it: one source for one figure, and this one is the source the card
 * is built from anyway.
 *
 * `enabled` IS THE CAPABILITY GATE, not a convenience. Without `payment.view`
 * the request is never made — the server would refuse it, and asking would only
 * write a denial into the audit log (§2.1 audits denied attempts too).
 */
export function useInvoicePayments(
  invoiceId: string | number | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<InvoicePaymentsResponse> {
  const id = invoiceId === undefined ? "" : String(invoiceId);

  return useQuery({
    queryKey: paymentKeys.invoice(id),
    queryFn: () => api.get<InvoicePaymentsResponse>(endpoints.invoicePayments(id)),
    enabled: id !== "" && (options.enabled ?? true),
    /*
     * Thirty seconds. Unlike the invoice document — immutable once issued, and
     * cached for five minutes on those grounds — the ledger moves whenever
     * anybody records a receipt, including his son on the shop computer. It goes
     * stale by events this screen did not cause.
     */
    staleTime: 30_000,
  });
}

/**
 * `GET /payments/outstanding` — every ISSUED bill with something still owed,
 * the same rows grouped by buyer, and the business-wide total.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO PAGING, AND THAT IS THE ENDPOINT'S DECISION, NOT AN OMISSION HERE. The
 * whole point of the figure is that it is complete: a total over page 1 of 4 is
 * a smaller number than the truth, presented as the truth. So there is no
 * `limit`, no `offset` and no filter bag — and therefore nothing to key by.
 *
 * IT IS ALSO THE UNPAID REGISTER'S ONLY POSSIBLE SOURCE. "Outstanding" is
 * `grand_total_paise` − `SUM(payments.amount_paise)`, which no `where` on
 * `GET /invoices` can express — Prisma cannot filter on an aggregate of a
 * related table. Filtering the fifty rows of one register page in the client
 * would look exactly like a working filter and silently miss every unpaid bill
 * on the pages it had not fetched, which is the failure this codebase already
 * has a `list-filter-parity` test for.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function useOutstanding(
  options: { enabled?: boolean } = {},
): UseQueryResult<OutstandingResponse> {
  return useQuery({
    queryKey: paymentKeys.outstanding(),
    queryFn: () => api.get<OutstandingResponse>(endpoints.paymentsOutstanding),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
  });
}

/**
 * `GET /customers/:id/statement` — one buyer's outstanding, the bills behind it
 * and the receipts against them.
 *
 * A SCREEN, NOT A PDF (§17.5). Rendering a statement as a printable document is
 * deliberately out of this pass rather than half-built: a statement that prints
 * is something a customer treats as a demand, and it would need its own §9-style
 * layout decisions and its own place in the PDF router.
 */
export function useCustomerStatement(
  customerId: string | number | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<CustomerStatementResponse> {
  const id = customerId === undefined ? "" : String(customerId);

  return useQuery({
    queryKey: paymentKeys.statement(id),
    queryFn: () => api.get<CustomerStatementResponse>(endpoints.customerStatement(id)),
    enabled: id !== "" && (options.enabled ?? true),
    staleTime: 30_000,
  });
}

/** What `POST /invoices/:id/payments` answers. */
export interface RecordPaymentResult {
  data: PaymentResponse;
  summary: InvoicePaymentSummary;
}

/**
 * `POST /invoices/:id/payments` — record money received (§17.5).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE BODY IS `PaymentCreateSchema`, THE SAME OBJECT THE SERVER VALIDATES WITH,
 * and it is `.strict()`: `invoiceId` is NOT a field on it, because the invoice
 * comes from the URL and a body naming a different one must not be able to pay
 * the wrong bill. `amountPaise` is INTEGER PAISE and strictly positive.
 *
 * NOTHING DERIVED IS SENT. No outstanding figure, no resulting balance, no
 * status. The server locks the invoice row, refuses a cancelled or draft bill,
 * sums the existing ledger and refuses an overpayment — all inside one
 * transaction, because two receipts that each pass the check separately must not
 * both land (§17.5 rule C). The client's job is to send what was received.
 *
 * A **409** IS INFORMATION, NOT A TRANSIENT FAILURE. It means the bill is
 * cancelled, or that this amount would take the total past the grand total, and
 * the server's message names the outstanding amount already Indian-grouped.
 * Show that sentence; do not retry it, and do not compose a second one.
 *
 * THE INVALIDATION IS THE POINT OF THE HOOK. A recorded receipt changes the
 * ledger on this screen, the outstanding register, the customer's statement AND
 * the home screen's "who owes me money" line — none of which this component
 * imports. `paymentKeys.all` catches the first three; `queryKeys.dashboard.all`
 * catches the last. The invoice DOCUMENT is deliberately not invalidated: being
 * paid changes nothing the invoice says.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function useRecordPayment(invoiceId: string | number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: PaymentCreate) =>
      api.post<RecordPaymentResult>(endpoints.invoicePayments(invoiceId), input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

/**
 * `DELETE /payments/:id` — remove a row that records something that never
 * happened.
 *
 * AN ENTRY-ERROR FIX, AND IT IS NOT A REFUND. §17.5 models no refunds and
 * forbids inventing a negative payment to stand for one: every outstanding
 * figure in the system is a `SUM` over this ledger, so a single negative row
 * would make a customer appear owed money he was never billed. There is
 * therefore no "reverse a payment" anywhere in this feature, and no reason field
 * on the delete — nothing was ever shown to a customer to retract. The audit row
 * (`payment.delete`, written in the same transaction) is what survives it.
 *
 * Answers the invoice's position AFTER the removal, so the caller can show where
 * the bill now stands without a second read.
 */
export function useDeletePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentId: number | string) =>
      api.delete<{ summary: InvoicePaymentSummary }>(endpoints.payment(paymentId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}
